// Backup and restore for tools that autosave in the browser (useStored). Nothing leaves the device: the file is
// built and read locally. Only keys under the tool's own prefixes are ever written back, whatever the file says.

export interface BackupFile { app: "freedailypro"; version: 1; tool: string; saved: string; data: Record<string, unknown> }
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

const MAX_BYTES = 5_000_000;
const allowed = (k: string, prefixes: string[]) => prefixes.some((p) => k.startsWith(p));

export function keysFor(prefixes: string[], store: Store): string[] {
  const out: string[] = [];
  for (let i = 0; i < store.length; i++) { const k = store.key(i); if (k && allowed(k, prefixes)) out.push(k); }
  return out.sort();
}

export function buildBackup(tool: string, prefixes: string[], store: Store, now = new Date()): BackupFile {
  const data: Record<string, unknown> = {};
  for (const k of keysFor(prefixes, store)) {
    const raw = store.getItem(k);
    if (raw == null) continue;
    try { data[k] = JSON.parse(raw); } catch { /* skip anything that isn't JSON */ }
  }
  return { app: "freedailypro", version: 1, tool, saved: now.toISOString(), data };
}

export type ParseResult = { ok: true; entries: Record<string, unknown> } | { ok: false; error: string };

export function parseBackup(text: string, tool: string, prefixes: string[]): ParseResult {
  if (text.length > MAX_BYTES) return { ok: false, error: "That file is too large to be a backup from this tool." };
  let j: unknown;
  try { j = JSON.parse(text); } catch { return { ok: false, error: "That isn't a backup file (it isn't valid JSON)." }; }
  const f = j as Partial<BackupFile> | null;
  if (!f || typeof f !== "object" || f.app !== "freedailypro" || !f.data || typeof f.data !== "object") return { ok: false, error: "That isn't a FreeDailyPro backup file." };
  if (f.tool !== tool) return { ok: false, error: `That backup is from a different tool${typeof f.tool === "string" ? ` (${f.tool})` : ""}.` };
  const entries: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f.data)) if (allowed(k, prefixes)) entries[k] = v;
  if (!Object.keys(entries).length) return { ok: false, error: "That backup has no saved data for this tool." };
  return { ok: true, entries };
}

/** Replace this tool's saved data with the backup's. Returns how many items were written. */
export function applyBackup(entries: Record<string, unknown>, prefixes: string[], store: Store): number {
  for (const k of keysFor(prefixes, store)) store.removeItem(k);
  let n = 0;
  for (const [k, v] of Object.entries(entries)) { store.setItem(k, JSON.stringify(v)); n++; }
  return n;
}

export function clearSaved(prefixes: string[], store: Store): void {
  for (const k of keysFor(prefixes, store)) store.removeItem(k);
}
